// src/pages/FileReducer/FileReducerPage.tsx
// Clean, Google-inspired File Reducer with SEO optimization

import React, { useState, useRef } from 'react';
import SEO from '../../../components/SEO/SEO';
import { ToolSchema, BreadcrumbSchema, HowToSchema } from '../../../components/Schema';
import { seoConfig } from '../../../config/seo';
import {
    CheckCircle, Shield, Zap, FileDown,
    Settings as SettingsIcon, RefreshCw, Info,
    Image as ImageIcon, Lock
} from 'lucide-react';
import FileUploader from '../../../components/Shared/FileUploader';
import ProcessingOptions from '../../../components/FileReducer/ProcessingOptions';
import ResultsDisplay from '../../../components/Shared/ResultsDisplay';
import DownloadButton from '../../../components/Shared/DownloadButton';
import ErrorMessage, { ErrorType } from '../../../components/ErrorMessage/ErrorMessage';
import { uploadFile, processFile, ReducerResponse } from '../../../services/toolsAPI/fileReducerAPI';
import SecurityBadge from '../../../components/Shared/SecurityBadge/SecurityBadge';
import { toast } from 'react-toastify';
import {
    ToolPageLayout,
    ToolInfoStrip,
    ToolSection,
    ToolStepCard,
    ToolFeatureCard,
    ToolFaqCta,
    ToolRelatedCard,
} from '../components';

type Stage = 'upload' | 'options' | 'results';

interface Options {
    quality: number;
    method: string;
    autoMode: boolean;
    compressionLevel: number;
    removeMetadata?: boolean;
    autoResize?: boolean;
    [key: string]: any;
}

interface AppError {
    type: ErrorType;
    message: string;
    details?: string;
}

const FileReducerPage: React.FC = () => {
    const [stage, setStage] = useState<Stage>('upload');
    const [file, setFile] = useState<File | null>(null);
    const [fileId, setFileId] = useState<string | null>(null);
    const [options, setOptions] = useState<Options>({
        quality: 80,
        method: 'auto',
        autoMode: true,
        compressionLevel: 50
    });
    const [result, setResult] = useState<ReducerResponse | null>(null);
    const [error, setError] = useState<AppError | null>(null);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number | null>(null);
    const [compressionFeedback, setCompressionFeedback] = useState<{ type: string; message: string; details: string } | null>(null);

    // Background upload tracking
    const uploadPromiseRef = useRef<Promise<{ fileId: string }> | null>(null);
    const uploadCompleteRef = useRef<boolean>(false);
    const uploadResultRef = useRef<{ fileId: string } | null>(null);

    const errorMessages = {
        fileTooLarge: "{{filename}} exceeds the maximum size limit of {{maxSize}}. Please upload files under 100MB.",
        fileInvalidType: "{{filename}} is not supported. Please upload JPG, PNG, GIF, WebP, PDF, DOC or DOCX files."
    };

    const getFileTypeOptions = (fileType: string) => {
        const commonOptions: any = {
            autoMode: {
                label: 'Auto Mode',
                type: 'checkbox',
                value: options.autoMode,
                description: 'Automatically select best compression settings'
            },
            removeMetadata: {
                label: 'Privacy Shield',
                type: 'checkbox',
                value: options.removeMetadata !== false,
                description: 'Scrub metadata, EXIF, and GPS data'
            }
        };

        if (fileType.includes('image')) {
            return {
                ...commonOptions,
                autoResize: {
                    label: 'Smart Scaling',
                    type: 'checkbox',
                    value: options.autoResize !== false,
                    description: 'Resize to 2048px (Recommended for web)'
                },
                ...(options.autoMode ? {} : {
                    compressionLevel: {
                        label: 'Compression Strategy',
                        type: 'button-group',
                        options: [
                            { label: 'Lossless', value: 10, description: 'Best quality' },
                            { label: 'Balanced', value: 50, description: 'Optimized' },
                            { label: 'Extreme', value: 90, description: 'Smallest size' }
                        ],
                        value: options.compressionLevel || 50
                    }
                })
            };
        } else if (fileType.includes('pdf')) {
            return {
                ...commonOptions,
                ...(options.autoMode ? {} : {
                    quality: {
                        label: 'Image Quality',
                        type: 'range',
                        min: 30,
                        max: 100,
                        step: 5,
                        value: options.quality || 80,
                        description: 'Downsamples images inside PDF'
                    }
                })
            };
        }

        return commonOptions;
    };

    const handleFileUpload = async (files: File | File[]) => {
        const selectedFile = Array.isArray(files) ? files[0] : files;
        if (!selectedFile) return;

        setError(null);
        setFile(selectedFile);
        setStage('options');

        // Start upload in background (non-blocking)
        uploadCompleteRef.current = false;
        uploadResultRef.current = null;

        const uploadPromise = uploadFile(selectedFile, (percent) => {
            setUploadProgress(percent);
        })
            .then((response) => {
                uploadCompleteRef.current = true;
                uploadResultRef.current = response;
                setFileId(response.fileId);
                return response;
            })
            .catch((err) => {
                uploadCompleteRef.current = true;
                const errorMsg = err.message || 'File upload failed';
                setError({ type: 'file', message: 'File upload failed', details: errorMsg });
                setStage('upload');
                toast.error(errorMsg);
                throw err;
            });

        uploadPromiseRef.current = uploadPromise;
    };

    const handleProcess = async () => {
        try {
            setLoading(true);
            setError(null);
            setCompressionFeedback(null);

            // Wait for upload to finish if still in progress
            let currentFileId = fileId;
            if (!uploadCompleteRef.current && uploadPromiseRef.current) {
                // Upload still in progress — show progress
                setUploadProgress((prev) => prev ?? 0);
                try {
                    const result = await uploadPromiseRef.current;
                    currentFileId = result.fileId;
                } catch {
                    setLoading(false);
                    return; // Error already handled in handleFileUpload
                }
            }

            setUploadProgress(null);

            if (!currentFileId) {
                setError({ type: 'file', message: 'Upload not complete', details: 'Please wait for the file to finish uploading.' });
                setLoading(false);
                return;
            }

            const processOptions = { ...options };
            if (!options.autoMode) {
                const level = options.compressionLevel || 50;
                processOptions.method = level < 30 ? 'minimal' : level < 60 ? 'balanced' : 'aggressive';
            } else {
                processOptions.method = 'auto';
            }

            const result = await processFile(currentFileId, processOptions);

            if (result.percentReduction <= 0) {
                setCompressionFeedback({
                    type: 'no-reduction',
                    message: 'No compression achieved',
                    details: 'This file may already be optimized. Try different settings or another file.'
                });
                toast.info('No further compression possible with current settings');
            } else {
                toast.success(`Compressed by ${result.percentReduction}%!`);
            }

            setResult(result);
            setStage('results');
        } catch (err: any) {
            const errorMsg = err.message || 'Processing failed';
            setError({ type: 'server', message: 'Processing failed', details: errorMsg });
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setStage('upload');
        setFile(null);
        setFileId(null);
        setOptions({ quality: 80, method: 'auto', autoMode: true, compressionLevel: 50 });
        setResult(null);
        setCompressionFeedback(null);
        setUploadProgress(null);
        uploadPromiseRef.current = null;
        uploadCompleteRef.current = false;
        uploadResultRef.current = null;
        if (stage !== 'upload') {
            toast.info('Reset for new file');
        }
    };

    const handleBackToOptions = () => {
        setStage('options');
        setCompressionFeedback(null);
    };

    const typeOptions = file ? getFileTypeOptions(file.type) : {};

    return (
        <>
            <SEO {...seoConfig.fileReducer} />
            <ToolSchema
                name="File Size Reducer"
                description="Free online file compression tool. Reduce PDF, JPG, PNG, and document file sizes by up to 80% while maintaining quality."
                url="/tools/compress-pdf-images"
                applicationCategory="UtilitiesApplication"
                featureList={[
                    "Compress PDF files",
                    "Reduce image file sizes (JPG, PNG, WebP)",
                    "Compress Word, Excel, and PPTX documents",
                    "Maintain quality while compressing",
                    "Privacy Shield metadata removal",
                    "Smart Scaling for web optimization",
                    "100MB file size limit"
                ]}
            />
            <BreadcrumbSchema
                breadcrumbs={[
                    { name: 'Home', url: 'https://www.filefusion.app/' },
                    { name: 'Tools', url: 'https://www.filefusion.app/tools' },
                    { name: 'File Size Reducer', url: 'https://www.filefusion.app/tools/compress-pdf-images' }
                ]}
            />
            <HowToSchema
                name="How to Reduce File Size Online"
                description="Compress your files quickly and easily with our free online file reducer tool."
                totalTime="PT5M"
                steps={[
                    { name: "Upload Your File", text: "Click or drag and drop your PDF, image, or document file (up to 100MB)." },
                    { name: "Choose Compression Settings", text: "Select your level (Balanced, Maximum, Lossless) and optional Pro features like Privacy Shield." },
                    { name: "Start Compression", text: "Click the 'Compress File' button to start the optimization process." },
                    { name: "Download Result", text: "Download your optimized file and see the side-by-side quality comparison." }
                ]}
                tool={["Web Browser"]}
            />

            <ToolPageLayout
                icon={<FileDown size={36} />}
                title="Free File Size Reducer"
                subtitle="Professional-grade compression for PDF, images, and documents. Reduce size by up to 80% with lossless visuals."
                tags={(
                    <>
                        <SecurityBadge type="secure" />
                        <SecurityBadge type="deleted" />
                        <SecurityBadge type="private" />
                    </>
                )}
            >
                <div className="flex-1 min-w-0">
                    {error && (
                        <ErrorMessage
                            type={error.type}
                            message={error.message}
                            details={error.details}
                            onClose={() => setError(null)}
                        />
                    )}

                    <div className="flex flex-col gap-[24px]">
                        {/* Upload Stage */}
                        {stage === 'upload' && (
                            <div className="bg-[#ffffff] border-[1px] border-[#dadce0] rounded-[20px] p-[20px] sm:p-[32px] shadow-[0_4px_20px_rgba(0,0,0,0.04)] relative overflow-hidden transition-all duration-200 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-[12px] sm:gap-[20px] mb-[28px] pb-[20px] border-b-[1px] border-[#dadce0]">
                                    <div className="w-[32px] h-[32px] bg-[#1a73e8] text-white rounded-full flex items-center justify-center text-[0.875rem] font-bold shrink-0">1</div>
                                    <div>
                                        <h2 className="text-[1.25rem] font-semibold text-[#202124] m-0 mb-[4px]">Upload Your File</h2>
                                        <p className="text-[0.9375rem] text-[#5f6368] m-0">Select a PDF, image, or document to compress</p>
                                    </div>
                                </div>
                                <FileUploader
                                    onFileUpload={handleFileUpload}
                                    loading={loading}
                                    error={error}
                                    acceptedTypes={{
                                        'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
                                        'application/pdf': ['.pdf'],
                                        'application/msword': ['.doc'],
                                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                                        'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx']
                                    }}
                                    maxSizeMB={100}
                                    instructionText="Drag and drop your file here, or click to browse"
                                    supportedFormats="Supported: PDF, JPG, PNG, GIF, DOCX, XLSX, PPTX (Max 100MB)"
                                    customErrorMessages={errorMessages}
                                />
                            </div>
                        )}

                        {/* Options Stage */}
                        {stage === 'options' && (
                            <div className="bg-[#ffffff] border-[1px] border-[#dadce0] rounded-[20px] p-[20px] sm:p-[32px] shadow-[0_4px_20px_rgba(0,0,0,0.04)] relative overflow-hidden transition-all duration-200 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-[12px] sm:gap-[20px] mb-[28px] pb-[20px] border-b-[1px] border-[#dadce0]">
                                    <div className="w-[32px] h-[32px] bg-[#1a73e8] text-white rounded-full flex items-center justify-center text-[0.875rem] font-bold shrink-0">2</div>
                                    <div className="flex-1">
                                        <h2 className="text-[1.25rem] font-semibold text-[#202124] m-0 mb-[4px]">Choose Compression Settings</h2>
                                        <p className="text-[0.9375rem] text-[#5f6368] m-0">Select auto mode for optimal results or customize manually</p>
                                    </div>
                                    {uploadProgress !== null && uploadProgress < 100 ? (
                                        <div className="flex items-center gap-[8px] px-[12px] py-[6px] bg-[#e8f0fe] rounded-[20px] text-[0.8125rem] font-medium text-[#1a73e8] shrink-0">
                                            <div className="w-[14px] h-[14px] border-[2px] border-[#1a73e8] border-t-transparent rounded-full animate-spin" />
                                            Uploading... {uploadProgress}%
                                        </div>
                                    ) : uploadCompleteRef.current ? (
                                        <div className="flex items-center gap-[6px] px-[12px] py-[6px] bg-[#e6f4ea] rounded-[20px] text-[0.8125rem] font-medium text-[#1e8e3e] shrink-0">
                                            <CheckCircle size={14} />
                                            Ready
                                        </div>
                                    ) : null}
                                </div>
                                <ProcessingOptions
                                    file={file!}
                                    options={typeOptions}
                                    values={options}
                                    onChange={(newVals) => setOptions(newVals as Options)}
                                    onProcess={handleProcess}
                                    onBack={handleReset}
                                    loading={loading}
                                    processButtonText={
                                        loading && uploadProgress !== null
                                            ? `Uploading... ${uploadProgress}%`
                                            : 'Compress File'
                                    }
                                />
                            </div>
                        )}

                        {/* Results Stage */}
                        {stage === 'results' && (
                            <div className="bg-[#ffffff] border-[1px] border-[#dadce0] rounded-[20px] p-[20px] sm:p-[32px] shadow-[0_4px_20px_rgba(0,0,0,0.04)] relative overflow-hidden transition-all duration-200 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-[12px] sm:gap-[20px] mb-[28px] pb-[20px] border-b-[1px] border-[#dadce0]">
                                    <div className="w-[32px] h-[32px] bg-[#1a73e8] text-white rounded-full flex items-center justify-center text-[0.875rem] font-bold shrink-0">3</div>
                                    <div>
                                        <h2 className="text-[1.25rem] font-semibold text-[#202124] m-0 mb-[4px]">Your Compressed File is Ready</h2>
                                        <p className="text-[0.9375rem] text-[#5f6368] m-0">Download your optimized file below</p>
                                    </div>
                                </div>

                                {compressionFeedback && (
                                    <div className="flex items-start gap-[12px] p-[16px] bg-[#eef2ff] text-[#4338ca] border-[1px] border-[#e0e7ff] rounded-[12px] mb-[24px]">
                                        <Info size={20} />
                                        <div>
                                            <strong>{compressionFeedback.message}</strong>
                                            <p>{compressionFeedback.details}</p>
                                        </div>
                                    </div>
                                )}

                                <ResultsDisplay
                                    original={file}
                                    result={result as any}
                                    type="reducer"
                                    noSuccess={(result?.percentReduction || 0) <= 0}
                                />

                                <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-[16px] mt-[32px] pt-[24px] border-t-[1px] border-[#dadce0] [&>button]:w-full sm:[&>button]:w-auto">
                                    <DownloadButton
                                        result={result as any}
                                        toolType="reducer"
                                        fileName={result?.fileName}
                                    />
                                    <button
                                        className="inline-flex items-center justify-center gap-[8px] py-[10px] px-[20px] bg-[#ffffff] text-[#5f6368] border-[1px] border-[#dadce0] rounded-[4px] text-[0.9375rem] font-medium cursor-pointer transition-all duration-200 font-inherit hover:bg-[#f8f9fa] hover:border-[#1a73e8] hover:text-[#1a73e8]"
                                        onClick={handleBackToOptions}
                                    >
                                        <SettingsIcon size={18} />
                                        Adjust Settings
                                    </button>
                                    <button
                                        className="inline-flex items-center justify-center gap-[8px] py-[10px] px-[20px] bg-[#f1f3f4] text-[#5f6368] border-none rounded-[4px] text-[0.9375rem] font-medium cursor-pointer transition-all duration-200 font-inherit hover:bg-[#e8eaed]"
                                        onClick={handleReset}
                                    >
                                        <RefreshCw size={18} />
                                        New File
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </ToolPageLayout>

            <ToolInfoStrip>
                <ToolInfoStrip.Section variant="privacy">
                    <h3><Shield size={20} />Secure Compression</h3>
                    <p>Your files are processed securely. We use advanced encryption and automatically delete all uploads after 1 hour.</p>
                </ToolInfoStrip.Section>
                <ToolInfoStrip.Section>
                    <h3><Zap size={20} />Pro Tips</h3>
                    <ul>
                        <li>Higher quality = Larger file size</li>
                        <li>Batch processing saves time</li>
                        <li>Original dimensions preserved</li>
                    </ul>
                </ToolInfoStrip.Section>
                <ToolInfoStrip.Section>
                    <h3><CheckCircle size={20} />Performance</h3>
                    <p>Our compression engine reduces file size by up to 80% without visible quality loss, perfect for web and email.</p>
                </ToolInfoStrip.Section>
            </ToolInfoStrip>

            <ToolSection variant="light" title="How to Compress Files Online" grid="steps">
                <ToolStepCard number={1} title="Upload Files" description="Drag and drop your images or PDF documents. We support JPG, PNG, WebP, and PDF formats up to 100MB per file." />
                <ToolStepCard number={2} title="Select Quality" description={'Choose your compression level. Use "Balanced" for the best trade-off between file size and visual quality.'} />
                <ToolStepCard number={3} title="Download Compressed" description="Our advanced algorithm reduces file size while maintaining quality. Download your optimized files instantly." />
            </ToolSection>

            <ToolSection variant="muted" title="Advanced File Compression" description="Smart compression algorithms that significantly reduce file size while maintaining visual quality." grid="features">
                <ToolFeatureCard><Zap size={28} /><h3>Smart Compression</h3><p>Intelligent analysis determines the optimal compression ratio for each file, reducing size by up to 80% with no visible quality loss.</p></ToolFeatureCard>
                <ToolFeatureCard><ImageIcon size={28} /><h3>Multi-Format Support</h3><p>Compress JPG, PNG, WebP images and PDF documents in one go. No need to use different tools for different file types.</p></ToolFeatureCard>
                <ToolFeatureCard><Lock size={28} /><h3>Secure &amp; Private</h3><p>Your files are processed securely via HTTPS and deleted automatically after 1 hour. We never analyze or share your content.</p></ToolFeatureCard>
                <ToolFeatureCard><CheckCircle size={28} /><h3>Bulk Processing</h3><p>Upload and compress multiple files simultaneously. Save time by processing entire batches of images and documents at once.</p></ToolFeatureCard>
            </ToolSection>

            <ToolSection variant="muted" title="When to Use File Compression" grid="features">
                <ToolFeatureCard><h3>Email Attachments</h3><p>Reduce file sizes to meet email attachment limits. Compress large PDFs or high-resolution images before sending to ensure they deliver successfully.</p></ToolFeatureCard>
                <ToolFeatureCard><h3>Website Optimization</h3><p>Optimize images for faster website loading. Smaller images improve page speed, reduce bandwidth costs, and provide better user experience.</p></ToolFeatureCard>
                <ToolFeatureCard><h3>Cloud Storage</h3><p>Save cloud storage space by compressing files before upload. Keep more files in services like Google Drive, Dropbox, or OneDrive.</p></ToolFeatureCard>
                <ToolFeatureCard><h3>Document Sharing</h3><p>Share documents faster with colleagues and clients. Compressed PDFs and Word documents upload and download quickly while maintaining readability.</p></ToolFeatureCard>
            </ToolSection>

            <ToolSection variant="light" title="Frequently Asked Questions" grid="none">
                <div className="flex flex-col gap-[20px] mb-[32px]">
                    <ToolFeatureCard variant="faq"><h3>How much can I reduce my file size?</h3><p>Compression results vary by file type. Images typically compress 30-80% without visible quality loss. PDFs commonly reduce by 20-60%, and documents by 40-70%. Results depend on original file content and quality settings.</p></ToolFeatureCard>
                    <ToolFeatureCard variant="faq"><h3>Will compression affect my file quality?</h3><p>Our Auto Mode finds the optimal balance between size and quality. For images, compression is visually lossless in most cases. PDFs maintain text clarity and readability. You can adjust quality settings manually for more control.</p></ToolFeatureCard>
                    <ToolFeatureCard variant="faq"><h3>What file formats are supported?</h3><p>We support JPG, PNG, GIF, WebP images; PDF documents; and DOC/DOCX/XLSX/PPTX files. Maximum file size is 100MB for all users.</p></ToolFeatureCard>
                    <ToolFeatureCard variant="faq"><h3>Is my file secure during compression?</h3><p>Yes. Files are uploaded securely via HTTPS, processed on our servers, and automatically deleted immediately after you download the compressed version. We never store or access your file contents.</p></ToolFeatureCard>
                </div>
                <ToolFaqCta prompt="More questions?" to="/faq" label="Visit our FAQ page" />
            </ToolSection>

            <ToolSection variant="muted" title="Related Tools You Might Need" grid="related">
                <ToolRelatedCard to="/tools/merge-split-pdf" icon={<FileDown size={24} />} title="PDF Merge &amp; Split" description="Combine multiple PDFs or split large files into smaller parts" linkLabel="Try Tool" />
                <ToolRelatedCard to="/tools/compare-text" icon={<FileDown size={24} />} title="Text Comparison" description="Compare two text files side-by-side with diff highlighting" linkLabel="Try Tool" />
                <ToolRelatedCard to="/tools/scan-image-to-text" icon={<FileDown size={24} />} title="OCR Text Extractor" description="Extract text from images and scanned PDFs instantly" linkLabel="Try Tool" />
            </ToolSection>
        </>
    );
};

export default FileReducerPage;
